import { MeetingAttemptResponseV3 } from '@chilipiper/api-client/src/user-availability'
import {
  getAdditionalRequiredAttendeesWithFallback,
  getUserExpectedHost,
  getRoundRobinAndOwnershipExpectedHost,
  getRequiredUsersFromGroupLinkEnhanced,
  MeetingLimitSource,
  BookerInvitationSettings,
} from '../helpers'
import { AvailabilityOptions } from '../types'
import {
  LinkResourceEnhanced,
  OwnershipLinkRoutingResult,
  RoundRobinLinkRoutingResult,
  isWithDistribution,
  getRoutingResultAvailabilityOptions,
  getFirstAssignmentFromRoutingResult,
} from '../../load-meeting-attempt'

export const useAvailabilityOptionsFromMeetingAttempt = ({
  linkResource,
  meetingAttempt,
  linkMeetingLimit,
  bookerInvitationSettings,
  routingResult,
}: {
  linkResource?: LinkResourceEnhanced
  meetingAttempt?: MeetingAttemptResponseV3
  linkMeetingLimit?: MeetingLimitSource
  bookerInvitationSettings?: BookerInvitationSettings
  routingResult?: OwnershipLinkRoutingResult | RoundRobinLinkRoutingResult
}): AvailabilityOptions | undefined => {
  if (meetingAttempt?.type !== 'Available') {
    return undefined
  }

  const productFeature = meetingAttempt?.attempt.productFeature

  const additionalRequiredAttendees = getAdditionalRequiredAttendeesWithFallback({
    meetingAttemptAttendees: meetingAttempt?.attempt.additionalAttendees,
    fallbackAttendees: linkResource?.requiredAttendees,
  })

  if (
    bookerInvitationSettings &&
    bookerInvitationSettings.type === 'Invite' &&
    !bookerInvitationSettings.isOptional
  ) {
    additionalRequiredAttendees.push(bookerInvitationSettings.id)
  }

  // Handle individual scheduling links
  if (
    productFeature?.type === 'AdminIndividualSchedulingLink' ||
    productFeature?.type === 'PersonalIndividualSchedulingLink'
  ) {
    return {
      userIds: [productFeature.hostId, ...additionalRequiredAttendees],
      expectedHost: getUserExpectedHost(productFeature.hostId),
      meetingLimit: linkMeetingLimit,
    }
  }

  // Handle HandoffRouting
  if (productFeature?.type === 'HandoffRouting' && linkResource?.type === 'HandoffRouter') {
    const selectedAssignees = linkResource.selectedAssignees

    if (!selectedAssignees) {
      return undefined
    }

    const distributionAssignments = selectedAssignees.filter(
      assignee => assignee.type === 'FromDistribution' || assignee.type === 'UserFromDistribution'
    )
    const userOrOwnershipAssignments = selectedAssignees.filter(
      assignee => assignee.type === 'User'
    )

    const userIds = userOrOwnershipAssignments.map(assignee => assignee.id)

    // If no distribution assignments, return user-based structure
    if (!distributionAssignments.length) {
      const firstUserId = userIds[0]

      return {
        userIds: [...userIds, ...additionalRequiredAttendees],
        expectedHost: getUserExpectedHost(firstUserId),
        meetingLimit: {
          type: 'NoMeetingLimit',
        },
      }
    }

    const assignment =
      linkResource.hostInfo?.type === 'Assignee'
        ? selectedAssignees[linkResource.hostInfo.index]
        : linkResource.hostInfo

    const expectedHost =
      assignment?.type === 'FromDistribution' || assignment?.type === 'UserFromDistribution'
        ? getRoundRobinAndOwnershipExpectedHost({
            bookerInvitationSettings,
            firstAssignment: { distributionId: assignment.distributionId },
          })
        : getUserExpectedHost(assignment?.id ?? '')

    return {
      distributionAttendees: distributionAssignments.map(assignee => ({
        userIds: assignee.type === 'FromDistribution' ? assignee.users : [assignee.user],
        distributionId: assignee.distributionId,
        required: true,
      })),
      expectedHost,
      additionalRequiredAttendees: additionalRequiredAttendees,
      ownershipAndUsersAttendees: userIds,
    }
  }

  // Handle RoundRobinSchedulingLink
  if (
    productFeature?.type === 'RoundRobinSchedulingLink' &&
    linkResource?.type === 'RoundRobinSchedulingLink' &&
    routingResult &&
    isWithDistribution(routingResult)
  ) {
    const firstAssignment = getFirstAssignmentFromRoutingResult(routingResult)
    const expectedHost = getRoundRobinAndOwnershipExpectedHost({
      bookerInvitationSettings,
      host: linkResource.host,
      firstAssignment,
    })

    return getRoutingResultAvailabilityOptions({
      routingResult,
      expectedHost,
      additionalRequiredAttendees,
    })
  }

  // Handle OwnershipSchedulingLink
  if (
    productFeature?.type === 'OwnershipSchedulingLink' &&
    linkResource?.type === 'OwnershipSchedulingLink' &&
    routingResult
  ) {
    const firstAssignment = getFirstAssignmentFromRoutingResult(routingResult)

    const expectedHost = getRoundRobinAndOwnershipExpectedHost({
      bookerInvitationSettings,
      host: linkResource.host,
      firstAssignment,
    })

    return getRoutingResultAvailabilityOptions({
      routingResult,
      expectedHost,
      additionalRequiredAttendees,
    })
  }

  // Handle GroupSchedulingLink
  if (
    productFeature?.type === 'GroupSchedulingLink' &&
    linkResource?.type === 'GroupSchedulingLink'
  ) {
    const groupRequiredAttendees = getRequiredUsersFromGroupLinkEnhanced(
      linkResource,
      meetingAttempt?.attempt
    )

    return {
      userIds: groupRequiredAttendees,
      expectedHost: getUserExpectedHost(linkResource.hostId),
      meetingLimit: linkMeetingLimit,
    }
  }

  return undefined
}
